/**
 * One "best X" listicle.
 *
 * ── The block order is the citation shape, with one deliberate exception ─────
 *
 * H1 → standfirst → answer capsule → DISCLOSURE → the list → the pick → how it
 * was ordered → FAQ → the other lists.
 *
 * Everywhere else on this site the answer capsule is followed by the substance.
 * Here the disclosure comes between them, above the first entry, and it is the
 * one piece of chrome on the site that is allowed to interrupt. A reader who
 * learns in the footer that one of the six entries wrote the page has already
 * read the ranking believing something else, and the correction arrives too
 * late to be worth anything. Putting it before entry one costs a few seconds of
 * attention and is the only version that is actually a disclosure.
 *
 * ── Why the limitation is set in the same type as the strength ───────────────
 *
 * Every entry renders "Best for" and "What it cannot do" at identical weight.
 * The category convention is a confident recommendation and a downside in grey
 * six-point type, and readers have learned to read that as an advertisement —
 * which it is. Equal weight is what makes the format worth anything, so it is
 * enforced here in the markup rather than left to whoever writes the next
 * entry.
 *
 * ── The ordinal ─────────────────────────────────────────────────────────────
 *
 * Each entry carries its position as a number, matching the `ItemList` in the
 * head. A listicle whose order is invisible reads as a set of unranked cards,
 * and the ranking is the thing the reader came for.
 */

import type { ContentLang } from "@fazly/shared/contentLang";
import { Link } from "@tanstack/react-router";

import { BEST_SLUGS, bestPath } from "../../lib/best/index";
import type { BestCopy, BestSlug } from "../../lib/best/types";
import type { BestUi } from "../../lib/best/ui";
import { comparePath } from "../../lib/compare/index";
import { toolPath, toolsIndexPath } from "../../lib/tools/index";
import { ContentFooter } from "../ContentFooter";

export function BestPage({
  slug,
  copy,
  ui,
  lang,
  allCopy,
}: {
  slug: BestSlug;
  copy: BestCopy;
  ui: BestUi;
  lang: ContentLang;
  allCopy: Record<BestSlug, BestCopy>;
}) {
  const others = BEST_SLUGS.filter((other) => other !== slug);

  return (
    <article className="mx-auto w-full max-w-3xl px-6 py-12 sm:py-16">
      <h1 className="font-display text-[clamp(2rem,7vw,3rem)] leading-[1.1] font-bold tracking-tight text-ink">
        {copy.h1}
      </h1>
      <p className="mt-4 max-w-xl text-lg leading-relaxed text-ink2">
        {copy.standfirst}
      </p>

      <p className="mt-8 text-[17px] leading-[1.7] text-body">{copy.answer}</p>

      <section className="mt-8 border-l-[3px] border-brand pl-4">
        <h2 className="font-display text-xs font-bold tracking-[0.16em] text-scripture uppercase">
          {ui.disclosureLabel}
        </h2>
        <p className="mt-2 text-[15px] leading-relaxed text-ink2">
          {copy.disclosure}
        </p>
      </section>

      <section className="mt-10">
        <h2 className="font-display text-2xl font-bold tracking-tight text-ink">
          {ui.listHeading}
        </h2>
        <ol className="mt-5 flex flex-col gap-4">
          {copy.entries.map((entry, i) => (
            <li key={entry.name} className="brutal p-6">
              <div className="flex items-baseline gap-3">
                <span className="font-display text-2xl font-bold text-brand tabular-nums">
                  {i + 1}
                </span>
                <h3 className="font-display text-xl font-bold tracking-tight text-ink">
                  {entry.name}
                </h3>
              </div>

              <p className="mt-4 font-display text-xs font-bold tracking-[0.16em] text-scripture uppercase">
                {ui.bestForLabel}
              </p>
              <p className="mt-1.5 leading-relaxed text-body">
                {entry.bestFor}
              </p>

              <p className="mt-4 font-display text-xs font-bold tracking-[0.16em] text-scripture uppercase">
                {ui.limitationLabel}
              </p>
              <p className="mt-1.5 leading-relaxed text-body">
                {entry.limitation}
              </p>

              {(entry.compare || entry.tool) && (
                <p className="mt-4">
                  {entry.compare && (
                    <Link
                      to={comparePath(entry.compare, lang)}
                      className="font-display text-[15px] font-bold text-brand underline decoration-hair underline-offset-4 hover:decoration-brand"
                    >
                      {ui.readComparison}
                    </Link>
                  )}
                  {entry.tool && (
                    <Link
                      to={toolPath(entry.tool, lang)}
                      className="font-display text-[15px] font-bold text-brand underline decoration-hair underline-offset-4 hover:decoration-brand"
                    >
                      {ui.openTool}
                    </Link>
                  )}
                </p>
              )}
            </li>
          ))}
        </ol>
      </section>

      <section className="mt-10">
        <h2 className="font-display text-2xl font-bold tracking-tight text-ink">
          {ui.pickHeading}
        </h2>
        <p className="mt-4 text-[17px] leading-[1.7] text-body">{copy.pick}</p>
      </section>

      <section className="mt-10">
        <h2 className="font-display text-2xl font-bold tracking-tight text-ink">
          {ui.methodHeading}
        </h2>
        <p className="mt-4 text-[17px] leading-[1.7] text-body">
          {copy.method}
        </p>
      </section>

      <section className="mt-10">
        <h2 className="font-display text-2xl font-bold tracking-tight text-ink">
          {ui.faqHeading}
        </h2>
        {/* Questions as <h3>, not <dt> — same reason as ToolPage: a heading is
            what an extractor splits on and lifts as the question being answered,
            and a <dt> may not contain one. */}
        <div className="mt-4 flex flex-col gap-6">
          {copy.faq.map((item) => (
            <div key={item.q}>
              <h3 className="font-display text-lg font-bold text-ink">
                {item.q}
              </h3>
              <p className="mt-2 text-[17px] leading-[1.7] text-body">
                {item.a}
              </p>
            </div>
          ))}
        </div>
      </section>

      <section className="mt-12 border-t-[2.5px] border-ink pt-8">
        <p className="text-[17px] leading-relaxed text-body">{ui.tryFirst}</p>
        <Link
          to={toolsIndexPath(lang)}
          className="rim mt-4 inline-flex h-[54px] items-center justify-center bg-brand px-7 font-display text-[17px] font-bold text-onbrand"
        >
          {ui.toolsCta}
        </Link>

        <h2 className="mt-10 font-display text-xl font-bold tracking-tight text-ink">
          {ui.otherLists}
        </h2>
        <ul className="mt-3 flex flex-col gap-2">
          {others.map((other) => (
            <li key={other}>
              <Link
                to={bestPath(other, lang)}
                className="font-display font-bold text-brand underline decoration-hair underline-offset-4 hover:decoration-brand"
              >
                {allCopy[other].h1}
              </Link>
            </li>
          ))}
        </ul>
      </section>

      <ContentFooter lang={lang} />
    </article>
  );
}
